import { Disclosure } from "@headlessui/react";
import classNames from "clsx";
import request from "graphql-request";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { HiChevronDown } from "react-icons/hi";



import { urls } from "@shared/constants";
import { Text } from "@shared/elements";
import { useSubmissionCategoryPriceDescription } from "@shared/hooks";
import { docTitle } from "@shared/utils";

import { CategoriesCallPage, TCategoriesCallPage } from "__generated__/types";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";

function CategoryDescription({ currency, category }) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.Categories`);
  const categoryPriceDescription = useSubmissionCategoryPriceDescription(
    category,
    currency,
  );

  return (
    <>
      <Text as="p" className="whitespace-pre-line">
        {category.description.en}
      </Text>
      {categoryPriceDescription && (
        <>
          <Text as="h4" weight="semibold" className="mb-2 mt-4">
            {t("submissionFee")}:
          </Text>
          <Text as="p">
            {categoryPriceDescription.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </Text>
        </>
      )}
    </>
  );
}

type Props = {
  data: TCategoriesCallPage;
};

export default function Categories({ data }: Props) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.Categories`);

  return (
    <>
      <Head>
        <title key="title">
          {docTitle(t("documentTitle"), data.callPage?.call.title.en)}
        </title>
        <meta
          property="og:title"
          content={docTitle(t("documentTitle"), data.callPage?.call.title.en)}
          key="og-title"
        />
        <meta
          property="og:url"
          content={`${urls.callPage(data.callPage?.slug ?? "")}/categories`}
          key="og-url"
        />
      </Head>
      <div className="flex flex-col items-center mx-auto py-10 lg:py-14">
        <div className="w-full max-w-[700px] px-4 md:px-0 mx-auto divide-y-[1px] divide-gray-200">
          <Text as="h2" size="3xl" weight="bold">
            {t("documentTitle")}
          </Text>
          <dl className="mt-8 space-y-6 divide-y divide-gray-200">
            {data.callPage?.call.categories.map((category, index) => (
              <Disclosure
                as="div"
                key={category.id}
                className="pt-6"
                defaultOpen={index === 0}
              >
                {({ open }) => (
                  <>
                    <dt className="text-lg">
                      <Disclosure.Button className="text-left w-full flex justify-between items-start text-gray-400">
                        <Text
                          as="span"
                          size="lg"
                          weight="medium"
                          color="custom"
                          className="text-black"
                        >
                          {category.label.en}
                        </Text>
                        <span className="ml-6 h-7 flex items-center">
                          <HiChevronDown
                            className={classNames(
                              open ? "-rotate-180" : "rotate-0",
                              "h-6 w-6 transform",
                            )}
                            aria-hidden="true"
                          />
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel as="dd" className="mt-2 pr-12">
                      <CategoryDescription
                        currency={data.callPage?.call.currency}
                        category={category}
                      />
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths<{ id: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext) {
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.Categories`,
    ...useSubmissionCategoryPriceDescription.messages,
  ]);

  const slug: string = params?.slug as string;

  const data: TCategoriesCallPage | null = await request(
    `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
    CategoriesCallPage,
    { slug },
  );

  if (!data?.callPage) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }
  return {
    props: {
      data,
      messages,
    },
    revalidate: 60,
  };
}
